import React, { memo, useState, useEffect } from 'react';
import { Typography, Box, Stack, TextField, InputAdornment } from '@mui/material';
import { IoMdArrowDropdown, IoMdArrowDropup } from 'react-icons/io';
import { IoSearch } from 'react-icons/io5';
import TreeView from '../../Reusable/TreeView';

// Define the props interface
interface CategorySelectorProps {
	isOpen: boolean;
	onToggle: () => void;
	onSelectCategory?: (categoryId: string, categoryData?: any) => void;
	initialCategoryName?: string;
	selectedCategoryId?: string;
	isSubcategoryCreation?: boolean;
}

const CategorySelector = memo(({
	isOpen,
	onToggle,
	onSelectCategory,
	initialCategoryName,
	selectedCategoryId
}: CategorySelectorProps) =>
{
	const [ selectedCategoryName, setSelectedCategoryName ] = useState<string>(initialCategoryName || '');
	const [ searchTerm, setSearchTerm ] = useState<string>('');

	// Update category name when initialCategoryName changes
	useEffect(() =>
	{
		if (initialCategoryName)
		{
			setSelectedCategoryName(initialCategoryName);
		}
	}, [ initialCategoryName ]);

	// Create a wrapper function to handle the type mismatch
	const handleTreeViewSelect = (selectedId: string | string[], selectedData?: any) =>
	{
		// For radio selection, always get the single ID
		const categoryId = Array.isArray(selectedId) ? selectedId[0] : selectedId;

		// Only update if selection actually changed
		if (onSelectCategory && categoryId)
		{
			onSelectCategory(categoryId, selectedData);

			// Update the selected category name and close the dropdown
			if (selectedData && selectedData.name)
			{
				setSelectedCategoryName(selectedData.name);
				setTimeout(() => onToggle(), 100);
			}
		}
	};

	return (
		<>
			<Typography variant='body2' pb={1}>Category*</Typography>
			<Box mb={2} sx={{ border: '1px solid #CFD1D4', borderRadius: '6px' }} display={'flex'} flexDirection={'column'} p={'4px 12px'}>
				<Stack flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} p={0.5} sx={{ cursor: 'pointer' }} onClick={onToggle}>
					<Typography
						variant='body2'
						sx={{
							color      : selectedCategoryName ? '#24A4C4' : 'inherit',
							fontWeight : selectedCategoryName ? 500 : 400
						}}
					>
						{selectedCategoryName || 'Select'}
					</Typography>
					{isOpen ? <IoMdArrowDropup color='var(--primaryColor)'/> : <IoMdArrowDropdown color='var(--primaryColor)'/>}
				</Stack>
				{isOpen && (
					<>
						<TextField
							fullWidth
							margin='normal'
							size='small'
							placeholder='Search Categories / Subcategories'
							sx={{
								backgroundColor            : '#F4F4F4',
								borderRadius               : '4px',
								'& .MuiOutlinedInput-root' : {
									border                   : 'none',
									'& fieldset'             : { border: 'none' },
									'&:hover fieldset'       : { border: 'none' },
									'&.Mui-focused fieldset' : { border: 'none' }
								},
								'& .MuiInputBase-root' : { boxShadow: 'none' }
							}}
							onChange={(e) => setSearchTerm(e.target.value)}
							InputProps={{
								startAdornment : (
									<InputAdornment position='start'>
										<IoSearch />
									</InputAdornment>
								)
							}}
						/>
						<Box maxHeight={'300px'} sx={{ overflowY: 'auto' }}>
							<TreeView
								selectionType='radio'
								onSelect={handleTreeViewSelect}
								maxDepth={5} // Enforce the 5-level depth limit
								initialSelectedId={selectedCategoryId || undefined}
								searchTerm={searchTerm}
								disableUncategorized
								isSubcategory

							/>
						</Box>
					</>
				)}
			</Box>
		</>
	);
});

CategorySelector.displayName = 'CategorySelector';

export default CategorySelector;